import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  FORM_BRANDING_CHANGED_EVENT,
  FORM_BRANDING_KEY,
  isFormBuilderPath,
} from '../../components/views/FormView/sessionKeys';

/** Read the form-builder branding-visibility flags written by `FormView`.
 *  Returns the defaults (show everything) when there's no active form on
 *  the page — keeps legacy /landing and /submission/... routes untouched. */
function readBrandingFlags(): {
  hideAgencyLogo: boolean;
  hideAgentHeadshot: boolean;
} {
  if (typeof window === 'undefined') {
    return { hideAgencyLogo: false, hideAgentHeadshot: false };
  }
  try {
    const raw = sessionStorage.getItem(FORM_BRANDING_KEY);
    if (!raw) return { hideAgencyLogo: false, hideAgentHeadshot: false };
    const parsed = JSON.parse(raw);
    return {
      hideAgencyLogo: !!parsed?.hideAgencyLogo,
      hideAgentHeadshot: !!parsed?.hideAgentHeadshot,
    };
  } catch {
    return { hideAgencyLogo: false, hideAgentHeadshot: false };
  }
}

/** Form-builder branding overrides for header chrome (`NavHeader`,
 *  `ReferralHeader`). Re-reads on every `form-branding-changed` event so
 *  toggling visibility inside the same session reflects immediately.
 *
 *  `gateChrome` is true on a form-builder URL until `FormView` has populated
 *  `FORM_BRANDING_KEY`: on the very first paint sessionStorage is empty (SSR +
 *  pre-hydration), so rendering the default chrome would show and then hide
 *  pieces — a visible flicker. Legacy /landing keeps rendering instantly. */
export function useFormBrandingFlags() {
  const pathname = usePathname();
  const onFormBuilderPath = isFormBuilderPath(pathname);
  const [brandingFlags, setBrandingFlags] = useState(readBrandingFlags);
  const [brandingReady, setBrandingReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const refresh = () => {
      setBrandingFlags(readBrandingFlags());
      setBrandingReady(true);
    };
    refresh();
    window.addEventListener(FORM_BRANDING_CHANGED_EVENT, refresh);
    return () =>
      window.removeEventListener(FORM_BRANDING_CHANGED_EVENT, refresh);
  }, []);

  return {
    ...brandingFlags,
    gateChrome: onFormBuilderPath && !brandingReady,
  };
}
